<template>
  <!-- 聊天触发按钮 -->
  <button
    class="fixed bottom-6 right-6 w-14 h-14 bg-brand-gradient rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 active:scale-90 z-50"
    @click="toggleChat"
  >
    <Transition name="icon-switch" mode="out-in">
      <MessageCircle v-if="!isChatOpen" key="message" class="w-6 h-6 text-white mx-auto" />
      <X v-else key="close" class="w-6 h-6 text-white mx-auto" />
    </Transition>
  </button>

  <!-- 聊天窗口 -->
  <Transition name="chat-window">
    <div
      v-if="isChatOpen"
      class="fixed bottom-24 right-6 w-80 h-96 glass rounded-2xl shadow-2xl z-40 flex flex-col overflow-hidden"
    >
      <!-- 聊天头部 -->
      <div class="bg-brand-gradient p-4 text-white">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Bot class="w-5 h-5" />
            </div>
            <div>
              <h3 class="font-semibold">AI 助手</h3>
              <div class="flex items-center space-x-1">
                <div class="w-2 h-2 bg-green-400 rounded-full"></div>
                <span class="text-xs opacity-90">在线</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 聊天消息区 -->
      <div class="flex-1 p-4 overflow-y-auto space-y-4" ref="messagesContainer">
        <div
          v-for="message in messages"
          :key="message.id"
          :class="[
            'flex',
            message.type === 'user' ? 'justify-end' : 'justify-start'
          ]"
        >
          <!-- AI消息 -->
          <div v-if="message.type === 'ai'" class="flex space-x-2 max-w-s">
            <div class="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
              <Bot class="w-4 h-4 text-gray-600" />
            </div>
            <div class="bg-gray-100 p-3 rounded-xl rounded-tl-md">
              <p class="text-sm text-gray-800">{{ message.content }}</p>
              <span class="text-xs text-gray-500 mt-1 block">{{ formatTime(message.timestamp) }}</span>
            </div>
          </div>

          <!-- 用户消息 -->
          <div v-else class="flex space-x-2 max-w-xs">
            <div class="bg-brand-gradient p-3 rounded-xl rounded-tr-md">
              <p class="text-sm text-white">{{ message.content }}</p>
              <span class="text-xs text-white/70 mt-1 block">{{ formatTime(message.timestamp) }}</span>
            </div>
            <div class="w-6 h-6 bg-orange-200 rounded-full flex items-center justify-center flex-shrink-0">
              <User class="w-4 h-4 text-orange-700" />
            </div>
          </div>
        </div>

        <!-- AI回复中的加载动画 -->
        <div v-if="isAiTyping" class="flex justify-start">
          <div class="flex space-x-2 max-w-xs">
            <div class="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
              <Bot class="w-4 h-4 text-gray-600" />
            </div>
            <div class="bg-gray-100 p-3 rounded-xl rounded-tl-md">
              <div class="flex space-x-1">
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 输入框 -->
      <div class="p-4 border-t border-gray-200">
        <div class="flex space-x-2">
          <input
            v-model="inputMessage"
            type="text"
            placeholder="输入您的问题..."
            class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-500 outline-none text-sm"
            @keyup.enter="sendMessage"
            @input="handleTyping"
          />
          <button
            class="w-10 h-10 bg-brand-gradient rounded-lg flex items-center justify-center hover:shadow-md transition-all duration-200 disabled:opacity-50"
            :disabled="!inputMessage.trim() || isAiTyping"
            @click="sendMessage"
          >
            <Send class="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, reactive, nextTick, onMounted } from 'vue'
import { MessageCircle, X, Bot, User, Send } from 'lucide-vue-next'

interface Message {
  id: string
  type: 'user' | 'ai'
  content: string
  timestamp: Date
}

// 响应式数据
const isChatOpen = ref(false)
const inputMessage = ref('')
const isAiTyping = ref(false)
const messagesContainer = ref<HTMLElement>()

const messages = reactive<Message[]>([
  {
    id: '1',
    type: 'ai',
    content: '您好！我是CatParty的AI助手，很高兴为您服务。我可以帮助您了解产品信息、回答猫咪护理问题，或为您推荐合适的产品。请问有什么可以帮助您的吗？',
    timestamp: new Date()
  }
])

// AI回复模板
const aiResponses = [
  '感谢您的问题！让我为您查找相关信息...',
  '这是一个很好的问题！基于我的知识库，我建议...',
  '对于猫咪的这个问题，通常有几种解决方案...',
  '根据您的描述，我推荐您可以尝试...',
  '这种情况在猫咪中比较常见，建议您...'
]

// 方法
const toggleChat = () => {
  isChatOpen.value = !isChatOpen.value
}

const sendMessage = async () => {
  if (!inputMessage.value.trim() || isAiTyping.value) return

  // 添加用户消息
  const userMessage: Message = {
    id: Date.now().toString(),
    type: 'user',
    content: inputMessage.value.trim(),
    timestamp: new Date()
  }
  messages.push(userMessage)

  // 清空输入框
  const messageContent = inputMessage.value
  inputMessage.value = ''

  // 滚动到底部
  await nextTick()
  scrollToBottom()

  // 模拟AI回复
  simulateAiResponse(messageContent)
}

const simulateAiResponse = async (userMessage: string) => {
  isAiTyping.value = true

  // 模拟AI思考时间
  await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000))

  // 生成AI回复
  let aiReply = ''
  
  if (userMessage.includes('猫咪') || userMessage.includes('猫')) {
    aiReply = '关于猫咪的问题，我很乐意为您解答！猫咪是非常可爱和聪明的宠物。如果您有具体的护理问题，建议您访问我们的AI医生功能获得更专业的建议。'
  } else if (userMessage.includes('产品') || userMessage.includes('购买')) {
    aiReply = '我们有丰富的猫咪用品供您选择！您可以访问我们的商店页面查看最新产品。如果需要个性化推荐，请告诉我您猫咪的年龄、品种和特殊需求。'
  } else if (userMessage.includes('健康') || userMessage.includes('医生')) {
    aiReply = '对于猫咪健康问题，我建议您使用我们的AI医生功能。那里有更专业的健康评估工具，可以根据症状提供详细的建议。当然，严重问题还是建议咨询专业兽医。'
  } else {
    aiReply = aiResponses[Math.floor(Math.random() * aiResponses.length)]
  }

  // 添加AI回复
  const aiMessage: Message = {
    id: Date.now().toString(),
    type: 'ai',
    content: aiReply,
    timestamp: new Date()
  }
  
  isAiTyping.value = false
  messages.push(aiMessage)

  // 滚动到底部
  await nextTick()
  scrollToBottom()
}

const handleTyping = () => {
  // 可以添加输入状态处理
}

const formatTime = (date: Date) => {
  return date.toLocaleTimeString('zh-CN', { 
    hour: '2-digit', 
    minute: '2-digit' 
  })
}

const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

onMounted(() => {
  // 组件初始化
})
</script>

<style scoped>
/* 图标切换动画 */
.icon-switch-enter-active,
.icon-switch-leave-active {
  transition: all 0.2s ease;
}

.icon-switch-enter-from {
  opacity: 0;
  transform: rotate(90deg) scale(0.8);
}

.icon-switch-leave-to {
  opacity: 0;
  transform: rotate(-90deg) scale(0.8);
}

/* 聊天窗口动画 */
.chat-window-enter-active {
  transition: all 0.3s ease;
}

.chat-window-leave-active {
  transition: all 0.3s ease;
}

.chat-window-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.chat-window-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

/* 自定义滚动条 */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>